/**
 * Pure helpers for an asset's tracking link (see `tracking-links-api.ts`: spark-creatives mints and stores the link).
 *
 * The front end computes only the CASINO destination: the lead promotion's on-site landing path (`cta_href`), which
 * lives on the pool {@link CreativeItem} (it is dropped from the persisted `StoryboardPromotion`), resolved to an
 * absolute URL under the brand's own domain, else the brand homepage. A landing-page destination comes from the
 * brand's landing pages (`useLandingDestinations`). The rest compares and labels destinations for the picker.
 *
 * These are pure so the resolution/naming logic is testable without rendering or network.
 */
import type { StoryboardPromotion } from '@/store/slices/creativeJobs';
import type { CreativeItem } from '@/nova/pages/creatives/creative-api';

/** An absolute `https://<host>` origin from a brand domain (which may already carry a scheme). */
export const siteOrigin = (brandDomain?: string): string | undefined => {
  const domain = brandDomain?.trim();
  if (!domain) return undefined;
  const withScheme = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
  return withScheme.replace(/\/+$/, '');
};

/**
 * The campaign destination for a video ad: the lead promotion's `cta_href` resolved to an absolute
 * URL under the brand domain, else the brand homepage. Returns `undefined` only when the brand has no
 * domain at all (nothing to point a campaign at). `cta_href` is matched back to the promotion by
 * `banner_id` from the creative pool, since the promotion object itself does not carry it.
 */
export const resolveCampaignDestination = (
  promotions: StoryboardPromotion[],
  poolItems: CreativeItem[],
  brandDomain?: string,
): string | undefined => {
  const origin = siteOrigin(brandDomain);
  if (!origin) return undefined;

  const leadBannerId = promotions.find((p) => p.banner_id)?.banner_id;
  const ctaHref = leadBannerId
    ? poolItems.find((item) => item.banner_id === leadBannerId)?.cta_href?.trim()
    : undefined;
  if (!ctaHref) return origin; // homepage fallback

  if (/^https?:\/\//i.test(ctaHref)) return ctaHref; // already absolute (rare)
  const path = ctaHref.startsWith('/') ? ctaHref : `/${ctaHref}`;
  return `${origin}${path}`;
};

/** LEGACY: the `external_ref` prefix of the video-ad campaigns minted on the affiliates platform before spark-creatives
 *  owned tracking links. Still read by the Affiliates page to put a video's thumbnail on its old campaigns. */
export const VIDEO_CAMPAIGN_REF_PREFIX = 'creatives-video';

/** LEGACY: the affiliates-platform campaign `external_ref` of one rendered video version (`<prefix>:<versionKey>`).
 *  New links never use it (see {@link VIDEO_CAMPAIGN_REF_PREFIX}). */
export const videoCampaignExternalRef = (versionKey: string, prefix: string = VIDEO_CAMPAIGN_REF_PREFIX): string =>
  `${prefix}:${versionKey}`;

/** A version key that is an epoch in milliseconds (`versionKey(created_at)`) as `2026-10-09 14:32` (UTC, so the
 *  stored name never depends on who made the link); '' for any other key (e.g. a landing page's own `page`). Pure. */
export const versionStamp = (versionKey: string): string => {
  if (!/^\d{10,}$/.test(versionKey)) return '';
  const at = new Date(Number(versionKey));
  return Number.isNaN(at.getTime()) ? '' : at.toISOString().slice(0, 16).replace('T', ' ');
};

/** A tracking link's display name (stored with the link, shown wherever its stats are listed): the asset's title,
 *  else its lead promotion's headline, else `fallback`; then the version's time when the key carries one
 *  (`Welcome spins · 2026-10-09 14:32`). Pure. */
export const trackingLinkName = (
  adName: string | undefined,
  promotions: readonly StoryboardPromotion[],
  versionKey: string,
  fallback: string,
): string => {
  const title =
    adName?.trim() || promotions.find((p) => p.headline?.trim())?.headline?.trim() || fallback.trim() || 'Tracking link';
  const stamp = versionStamp(versionKey);
  return stamp ? `${title} · ${stamp}` : title;
};

/** A URL compared as a destination: trimmed, its trailing slashes dropped (before any query / fragment). Pure. */
const destinationKey = (url?: string): string => {
  const value = url?.trim() ?? '';
  const cut = value.search(/[?#]/);
  const path = cut === -1 ? value : value.slice(0, cut);
  const rest = cut === -1 ? '' : value.slice(cut);
  return `${path.replace(/\/+$/, '')}${rest}`;
};

/** Whether two destination URLs point at the same page (a trailing slash is not a difference). Blank never matches.
 *  Pure. */
export const sameDestination = (a?: string, b?: string): boolean => {
  const ka = destinationKey(a);
  return ka !== '' && ka === destinationKey(b);
};

/** A destination shown without its scheme or trailing slash (`lp.brand.com/welcome-wheel`). Pure. */
export const destinationHostPath = (url: string): string => destinationKey(url).replace(/^https?:\/\//i, '');

/** The picker label of a landing page: its name, then where it lives (`Welcome wheel · lp.brand.com/welcome-wheel`);
 *  the address alone when the page has no name. Pure. */
export const landingDestinationLabel = (name: string, url: string): string => {
  const where = destinationHostPath(url);
  const label = name.trim();
  return label ? `${label} · ${where}` : where;
};
